// Per-rule results from the model and the deterministic item verdict
// (ADR-0173 fase 2, PR-1 / PR-8).
//
// D1 as the plan assumes until the founder confirms (plan §Fase 2): the
// item verdict is the WORST of its photo-judgeable rule results —
//   no_cumple > parcial (→ con_obs) > no_determinable (→ con_obs at the
//   lightest severity) > cumple
// — and rules that need a datum or a person (`dato`, `humano`) are
// informative: they never travel to the model and never move the verdict.
// A rule with no result at all (the model skipped it) counts as
// no_determinable: silence is never compliance (ADR-0088).
import { z } from "zod";
export const RULE_RESULT_STATUSES = ["cumple", "parcial", "no_cumple", "no_determinable"];
export const ruleResultStatusSchema = z.enum(RULE_RESULT_STATUSES);
export const MISSING_EVIDENCE_REASONS = [
    "sin_evidencia",
    "foto_no_muestra",
    "requiere_dato",
    "requiere_humano",
];
export const missingEvidenceReasonSchema = z.enum(MISSING_EVIDENCE_REASONS);
/** What the model returns per rule (exploración §9). `ruleRef` is the
 *  short alias the prompt used (`P1`); the handler maps it back to the
 *  ruleVersionId and refuses an alias it did not send. */
export const modelRuleResultSchema = z
    .object({
    ruleRef: z.string().regex(/^P\d{1,3}$/),
    status: ruleResultStatusSchema,
    observed: z.string().trim().max(300).optional(),
    expected: z.string().trim().max(300).optional(),
    missingEvidence: missingEvidenceReasonSchema.optional(),
    photoIndices: z.array(z.number().int().nonnegative()).max(20).optional(),
    audioNoteIds: z.array(z.string().min(1)).max(20).optional(),
})
    .strict();
export const ITEM_VERDICTS = ["cumple", "con_obs", "no_cumple"];
const SEVERITY_ORDER = {
    no_cumple: 3,
    parcial: 2,
    no_determinable: 1,
    cumple: 0,
};
/** Deterministic: same results → same verdict. Pure, no I/O. */
export function deriveItemVerdict(results) {
    const judgeable = results.filter((r) => r.verification === "foto");
    const informative = results.length - judgeable.length;
    if (judgeable.length === 0) {
        // Nothing the model could judge: the item falls back to the legacy
        // per-item verdict upstream; here we can only say "not determinable".
        return { verdict: "con_obs", drivers: ["no_determinable"], informative };
    }
    const drivers = [...new Set(judgeable.map((r) => r.status))].sort((a, b) => SEVERITY_ORDER[b] - SEVERITY_ORDER[a]);
    const worst = drivers[0];
    const verdict = worst === "no_cumple" ? "no_cumple" : worst === "cumple" ? "cumple" : "con_obs";
    return { verdict, drivers, informative };
}
/** PR-9 — whether an item's rule results CITE a client rule against it (a
 *  rule judged `no_cumple` or `parcial`). A `no_cumple` on such an item is
 *  an automated proposal resting on the client's policy, so revisión B
 *  demands the supervisor confirm it item by item (Ley 21.719 art. 8 bis);
 *  core-api refuses the PATCH without that confirmation and the PWA asks
 *  for it — one predicate for both. */
export function ruleResultsCitePolicy(results) {
    return (results ?? []).some((r) => r.status === "no_cumple" || r.status === "parcial");
}
//# sourceMappingURL=rule-result.js.map